'use client';

import Image from 'next/image';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Scissors, Type, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Memo } from '../momento/MomentoContext';

interface MemoCardProps {
  memo: Memo;
  onClick: () => void;
  isSelected?: boolean;
  className?: string;
}

export function MemoCard({ memo, onClick, isSelected = false, className }: MemoCardProps) {
  const cutCount = memo.edit_plan?.cuts?.length || 0;
  const captionEditCount = memo.edit_plan?.captions?.length || 0;
  const duration = memo.source_timerange.end - memo.source_timerange.start;

  const statusConfig = {
    draft: { label: 'Draft', color: 'bg-momento-200 text-momento-700 dark:bg-momento-700 dark:text-momento-200' },
    exporting: { label: 'Exporting', color: 'bg-accent-blue-100 text-accent-blue-700 dark:bg-accent-blue-900/50 dark:text-accent-blue-300', icon: Loader2 },
    exported: { label: 'Ready', color: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300', icon: Check },
  };

  const status = statusConfig[memo.status as keyof typeof statusConfig] || statusConfig.draft;

  // Generate thumbnail URL if playbackId available
  const thumbnailUrl = memo.playbackId
    ? `https://image.mux.com/${memo.playbackId}/thumbnail.jpg?time=${memo.source_timerange.start}&width=400&height=225`
    : null;

  // Debug logging
  console.log('[MemoCard] Rendering:', {
    title: memo.title,
    hasPlaybackId: !!memo.playbackId,
    playbackId: memo.playbackId,
    thumbnailUrl,
    startTime: memo.source_timerange.start
  });

  return (
    <Card
      onClick={onClick}
      className={cn(
        'cursor-pointer transition-all hover:shadow-md overflow-hidden group',
        isSelected && 'ring-2 ring-accent-blue-500 bg-accent-blue-50/30 dark:bg-accent-blue-950/30',
        className
      )}
    >
      {/* Thumbnail Section */}
      {thumbnailUrl ? (
        <div className="relative w-full aspect-video bg-momento-100 dark:bg-momento-800">
          <Image
            src={thumbnailUrl}
            alt={memo.title}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 400px"
            unoptimized
          />
          {/* Status badge overlay */}
          <div className="absolute top-2 right-2">
            <Badge
              variant="secondary"
              className={cn('text-[10px] h-5 flex items-center gap-1 shadow-sm', status.color)}
            >
              {status.icon && <status.icon className="h-3 w-3" />}
              {status.label}
            </Badge>
          </div>
          {/* Duration overlay */}
          <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-0.5 rounded font-medium">
            {Math.round(duration)}s
          </div>
        </div>
      ) : (
        <div className="w-full aspect-video bg-momento-100 dark:bg-momento-800 flex items-center justify-center">
          <Clock className="h-8 w-8 text-momento-400" />
        </div>
      )}

      {/* Content Section */}
      <div className="p-3">
        {/* Title */}
        <h4 className="text-sm font-medium text-momento-900 dark:text-momento-50 line-clamp-2 mb-2 group-hover:text-accent-blue-600">
          {memo.title}
        </h4>

        {/* Meta Info */}
        <div className="flex items-center gap-3 text-xs text-momento-600 dark:text-momento-400">
          {cutCount > 0 && (
            <div className="flex items-center gap-1">
              <Scissors className="h-3 w-3 text-red-600 dark:text-red-400" />
              <span>{cutCount}</span>
            </div>
          )}
          {captionEditCount > 0 && (
            <div className="flex items-center gap-1">
              <Type className="h-3 w-3 text-amber-600 dark:text-amber-400" />
              <span>{captionEditCount}</span>
            </div>
          )}
          {!thumbnailUrl && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{Math.round(duration)}s</span>
            </div>
          )}
        </div>

        {/* Timestamp hint */}
        <div className="mt-2 text-[10px] text-momento-500 dark:text-momento-500 font-mono">
          {memo.source_timerange.start.toFixed(0)}s - {memo.source_timerange.end.toFixed(0)}s
        </div>
      </div>
    </Card>
  );
}
